// alert
import alert from '@drivesally/sally-ui/lib/libs/alert';

// branch components
import BranchAppReservationsPerHourModal from '../../components/BranchAppReservationsPerHourModal/BranchAppReservationsPerHourModal';
import BranchGeneralInfoStep from '../../components/BranchGeneralInfoStep/BranchGeneralInfoStep';

// branch api
import updateBranchApi from '@drivesally/sally-fn/lib/branch/api/update.api.branch';

// branch events
import branchSelectedEvent from '../../events/selected.event.branch';

// branch preparations
import generalBranchInfoPreparation from '../../preparations/generalInfo.preparation.branch';

// date lib
import convertHoursIntoHourMinutes from '../../../date/lib/convertHoursIntoHourMinutes.lib.date';

// error lib
import parseError from '@drivesally/sally-fw/lib/error/parseError';

// event HOCs
import subscriptionHOC from '@drivesally/sally-fw/lib/event/hoc/subscription.hoc.event';

// local constants
import ATTRIBUTES_TO_COPY from './constants/attributesToCopy.constant';

// local lib
import generateAttributes from './lib/generateAttributes.lib';

// notify
import notify from '@drivesally/sally-ui/lib/libs/notify';

// propTypes
import PropTypes from 'prop-types';

// react
import React, {Component, Fragment} from 'react';

class BranchGeneralInfoStepContainer extends Component {
  static propTypes = {
    branch: PropTypes.object,
    canEditName: PropTypes.bool,
    Container: PropTypes.any,
    goingBack: PropTypes.bool,
    onBack: PropTypes.func,
    onClose: PropTypes.func,
    onNext: PropTypes.func,
    originalBranch: PropTypes.object,
    setData: PropTypes.func,
    setStepChangeValidator: PropTypes.func,
    subscribe: PropTypes.func,
    visible: PropTypes.bool,
  };

  state = {
    ...generateAttributes(),
    towing_jump_services: [],
    app_reservations_per_hour_override: {},
    perHourOverrideDraft: {},
    perHourOverrideSaving: false,
    perHourOverrideVisible: false,
  };

  changedAttributes = [];

  componentDidMount() {
    const {setStepChangeValidator, subscribe} = this.props;
    this.initialize();
    subscribe(branchSelectedEvent.subscribe(this.branchSelected));
    setStepChangeValidator(this.stepChangeValidator);
  }

  initialize = (branch = this.props.branch) => {
    this.setState({
      ...generateAttributes(branch),
      towing_jump_services: branch?.towing_jump_services?.services || [],
      app_reservations_per_hour_override:
        branch?.app_reservations_per_hour_override || {},
    });
  };

  copySettings = (branch) => {
    this.changedAttributes = [
      ...Object.keys(generateAttributes(branch)),
      'towing_jump_services',
      'app_reservations_per_hour_override',
    ];
    this.initialize(branch);
  };

  branchSelected = ({branch}) => {
    const preparedBranch = [...ATTRIBUTES_TO_COPY].reduce(
      (combined, attribute) => ({
        ...combined,
        [attribute]: branch?.[attribute],
      }),
      !!this.props.branch ? {...this.props.branch} : {}
    );
    notify({
      id: 'changeBranch',
      title: 'Confirm',
      icon: undefined,
      content:
        'Are you sure you want to copy settings a different branch? This will undo this whole section.',
      primary: {
        label: 'Cancel',
        onClick: () => null,
      },
      secondary: {
        label: 'Continue',
        onClick: () => this.copySettings(preparedBranch),
      },
    });
  };

  continue = async () => {
    const {onNext} = this.props;
    const branch = await this.generateBranch(true);
    if (!branch) return;
    onNext({branch, changedAttributes: this.changedAttributes});
  };

  back = async () => {
    const {onBack} = this.props;
    const branch = await this.generateBranch(false);
    if (!branch) return;
    onBack({branch, changedAttributes: this.changedAttributes});
  };

  generateBranch = async (validate = false) => {
    const {branch} = this.props;
    try {
      const {
        towing_jump_services,
        app_reservations_per_hour_override,
        perHourOverrideDraft,
        perHourOverrideSaving,
        perHourOverrideVisible,
        ...attributes
      } = this.state;
      const preparedBranch = await generalBranchInfoPreparation({
        validate,
        ...attributes,
      });
      const filledTowingJumpServices = [...towing_jump_services].filter(
        (service) =>
          !!service?.name?.trim() ||
          !!service?.phone_number?.trim() ||
          !!service?.service_description?.trim()
      );
      const updatedBranch = {
        ...branch,
        ...preparedBranch,
        towing_jump_services: {services: filledTowingJumpServices},
        app_reservations_per_hour_override,
      };
      updatedBranch.tax_rate =
        Number(branch?.tax_rate || 0) === Number(updatedBranch?.tax_rate || 0)
          ? branch.tax_rate
          : updatedBranch.tax_rate;
      return updatedBranch;
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      return null;
    }
  };

  stepChangeValidator = async () => {
    const branch = await this.generateBranch(true);
    return {
      shouldContinue: !!branch,
      data: {branch, changedAttributes: this.changedAttributes},
    };
  };

  change = (value, key) => {
    if (!this.changedAttributes.includes(key))
      this.changedAttributes = [...this.changedAttributes, key];
    this.setState({[key]: value});
  };

  changeTowingJumpServices = (towing_jump_services) => {
    if (!this.changedAttributes.includes('towing_jump_services'))
      this.changedAttributes = [
        ...this.changedAttributes,
        'towing_jump_services',
      ];
    this.setState({towing_jump_services});
  };

  openPerHourOverride = () =>
    this.setState({
      perHourOverrideDraft: {...this.state.app_reservations_per_hour_override},
      perHourOverrideVisible: true,
    });

  closePerHourOverride = () => {
    if (this.state.perHourOverrideSaving) return;
    this.setState({perHourOverrideVisible: false});
  };

  changePerHourOverrideDraft = (perHourOverrideDraft) =>
    this.setState({perHourOverrideDraft});

  savePerHourOverride = async () => {
    const {branch, originalBranch, setData} = this.props;
    if (this.state.perHourOverrideSaving) return;
    const app_reservations_per_hour_override = {};
    for (const [day, hours] of Object.entries(
      this.state.perHourOverrideDraft
    )) {
      for (const [hour, limit] of Object.entries(hours || {})) {
        if (!/^\d+$/.test(`${limit}`.trim()))
          return alert.warning(
            `Enter the number of reservations for ${day} ${convertHoursIntoHourMinutes(
              Number(hour)
            )}`
          );
        app_reservations_per_hour_override[day] = {
          ...app_reservations_per_hour_override[day],
          [hour]: Number(`${limit}`.trim()),
        };
      }
    }

    // A new branch doesn't exist yet, so the overrides are saved with it
    if (!branch?.id) {
      if (
        !this.changedAttributes.includes('app_reservations_per_hour_override')
      )
        this.changedAttributes = [
          ...this.changedAttributes,
          'app_reservations_per_hour_override',
        ];
      this.setState({
        app_reservations_per_hour_override,
        perHourOverrideVisible: false,
      });
      return;
    }

    this.setState({perHourOverrideSaving: true});
    try {
      const updatedBranch = await updateBranchApi(branch.id, {
        app_reservations_per_hour_override,
      });
      const savedOverride =
        updatedBranch?.app_reservations_per_hour_override || {};
      setData({
        branch: {...branch, app_reservations_per_hour_override: savedOverride},
        originalBranch: {
          ...(originalBranch || branch),
          app_reservations_per_hour_override: savedOverride,
        },
      });
      this.setState({
        app_reservations_per_hour_override: savedOverride,
        perHourOverrideSaving: false,
        perHourOverrideVisible: false,
      });
      alert.success('Per hour app reservations saved');
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      this.setState({perHourOverrideSaving: false});
    }
  };

  render() {
    const {branch, canEditName, Container, onBack} = this.props;
    const {
      towing_jump_services,
      app_reservations_per_hour_override,
      perHourOverrideDraft,
      perHourOverrideSaving,
      perHourOverrideVisible,
      ...attributes
    } = this.state;
    return (
      <Fragment>
        <BranchGeneralInfoStep
          {...attributes}
          appReservationsPerHourOverride={app_reservations_per_hour_override}
          canEditName={canEditName}
          Container={Container}
          editMode={!!branch?.id}
          onBack={!!onBack ? this.back : null}
          onChange={this.change}
          onChangeTowingJumpServices={this.changeTowingJumpServices}
          onNext={this.continue}
          onOpenPerHourOverride={this.openPerHourOverride}
          towingJumpServices={towing_jump_services}
        />
        <BranchAppReservationsPerHourModal
          defaultReservationsPerHour={attributes.app_reservations_per_hour?.api?.format?.()}
          onSave={this.savePerHourOverride}
          saveLabel={!!branch?.id ? 'Save' : 'Apply'}
          saving={perHourOverrideSaving}
          onChange={this.changePerHourOverrideDraft}
          onClose={this.closePerHourOverride}
          override={perHourOverrideDraft}
          visible={perHourOverrideVisible}
        />
      </Fragment>
    );
  }
}

export default subscriptionHOC(BranchGeneralInfoStepContainer);
